import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { Sort, useSeeds } from '@/hooks/useSeeds';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { Preseed, ResourceType, SeedStatus } from '@/types';
import { useMutation } from '@/utils/api';
import { Regex } from '@/utils/regex.util';

export interface Filter {
  search?: string;
  advancedFilters?: Partial<Record<keyof Preseed, string[]>>;
}

/**
 * Legacy preseed hook - now an adapter to polymorphic useSeeds
 *
 * **Phase 3 Refactor**: This hook has been simplified from 126 lines to 20 lines
 * by delegating all logic to the polymorphic useSeeds hook.
 *
 * @deprecated This hook is deprecated and will be removed in v2.0.0
 *
 * Use `useSeeds({ type: 'preseed' })` directly for new code.
 * Kept for backward compatibility during migration.
 *
 * **Migration Path**:
 * ```typescript
 * // Before (using adapter)
 * const { data, status, refetch } = usePreseed({ search, sort });
 *
 * // After (use polymorphic hook directly)
 * const { data, status, refetch } = useSeeds({ type: 'preseed', search, sort });
 * ```
 *
 * @see /modules/chariot/ui/src/sections/seeds/MIGRATION.md
 *
 * @param props - Filter options including search, advancedFilters, enabled, sort
 * @returns Preseed data with count enrichment and query utilities
 */
export const usePreseed = (
  props?: Filter & { enabled?: boolean; sort?: Sort }
) => {
  const { search, advancedFilters, enabled = true, sort } = props || {};

  // Delegate to polymorphic useSeeds hook
  const seedsQuery = useSeeds({
    type: 'preseed',
    search,
    sort,
    advancedFilters,
    enabled,
  });

  // Transform response to match legacy interface
  // The data is already enriched with count information by useSeeds
  return {
    data: seedsQuery.data as Preseed[], // Cast to Preseed[] for backward compatibility
    error: seedsQuery.error,
    status: seedsQuery.status,
    fetchNextPage: seedsQuery.fetchNextPage,
    isFetchingNextPage: seedsQuery.isFetchingNextPage,
    isFetching: seedsQuery.isFetching,
    hasNextPage: seedsQuery.hasNextPage,
    isAllEmpty: seedsQuery.isAllEmpty,
    isFilteredEmpty: seedsQuery.isFilteredEmpty,
    invalidate: seedsQuery.refetch, // Map invalidate to refetch
    refetch: seedsQuery.refetch,
    isFiltersApplied: seedsQuery.isFiltersApplied,
  };
};

export const usePreseedsActions = () => {
  const { invalidate } = usePreseed({ enabled: false });
  const { mutateAsync: updatePreseed } = useUpdatePreseed();
  const { me, friend } = useAuth();
  const canApprove = Regex.PRAETORIAN_ACCOUNT.test(me || '');
  const isManagedAccount =
    Regex.MANAGED_ACCOUNT.test(me || '') ||
    Regex.MANAGED_ACCOUNT.test(friend || '');

  const handleApprove = async (preseeds: Preseed[]) => {
    const promises = preseeds.map(({ key }) =>
      updatePreseed({ key, status: SeedStatus.Accept })
    );
    await Promise.all(promises);
    invalidate();
  };

  const handleReject = async (preseeds: Preseed[]) => {
    const promises = preseeds.map(({ key }) =>
      updatePreseed({ key, status: SeedStatus.Reject })
    );
    await Promise.all(promises);
    invalidate();
  };
  return {
    onApprove: handleApprove,
    onReject: handleReject,
    canApprove,
    isManagedAccount,
  };
};

export function useUpdatePreseed() {
  const axios = useAxios();
  const { invalidate: invalidateGenericSearch } = useGenericSearch(
    {
      query: '',
      label: ResourceType.Preseed,
    },
    { enabled: false }
  );

  const { invalidate: invalidateMy } = useMy(
    {
      resource: 'preseed',
    },
    { enabled: false }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to update attributes',
    mutationFn: async ({
      key,
      status,
      comment,
    }: {
      key: string;
      status?: SeedStatus;
      comment?: string;
    }) => {
      const promise = axios.put('/preseed', { key, status, comment });

      toast.promise(promise, {
        loading: 'Updating attributes',
        success: 'Attributes updated successfully',
        error: 'Failed to update attributes',
      });

      await promise;

      queryClient.invalidateQueries({ queryKey: getQueryKey.getMy('preseed') });

      invalidateGenericSearch();
      invalidateMy();
    },
  });
}
